import { loanConditionApiV2 } from 'app/services/api';
import dayjs from 'dayjs';
import { normalize, schema } from 'normalizr';

import { CALENDAR_LOCAL_TIME_DATE_FORMAT } from 'constants/date';
import { LOAN_APPLICATION_ACTION_NAME_PREFIX } from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { TASKS_ACTION_TYPE } from './tasks.actions';

export const LOAN_CONDITIONS_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/loanConditions`,
  {
    GET_CONDITIONS_BY_LOAN_ID: 'GET_CONDITIONS_BY_LOAN_ID',
    GET_CONDITIONS_BY_LOAN_ID_PENDING_STATUS: 'GET_CONDITIONS_BY_LOAN_ID_PENDING_STATUS',
    GET_CONDITIONS_BY_LOAN_ID_REJECTED_STATUS: 'GET_CONDITIONS_BY_LOAN_ID_REJECTED_STATUS',
    GET_BROKER_CONDITIONS: 'GET_BROKER_CONDITIONS',
    GET_BROKER_CONDITIONS_REJECTED_STATUS: 'GET_BROKER_CONDITIONS_REJECTED_STATUS',
    GET_CONDITIONS_BY_IDS: 'GET_CONDITIONS_BY_IDS',
    GET_CONDITIONS_BY_IDS_PENDING_STATUS: 'GET_CONDITIONS_BY_IDS_PENDING_STATUS',
    GET_CONDITIONS_BY_IDS_REJECTED_STATUS: 'GET_CONDITIONS_BY_IDS_REJECTED_STATUS',
    GET_CONDITION_BY_CONDITION_ID: 'GET_CONDITION_BY_CONDITION_ID',
    GET_CONDITION_BY_CONDITION_ID_PENDING_STATUS: 'GET_CONDITION_BY_CONDITION_ID_PENDING_STATUS',
    GET_CONDITION_BY_CONDITION_ID_REJECTED_STATUS: 'GET_CONDITION_BY_CONDITION_ID_REJECTED_STATUS',
    SIGN_OFF_LOAN_CONDITION: 'SIGN_OFF_LOAN_CONDITION',
    UNSIGN_OFF_LOAN_CONDITION: 'UNSIGN_OFF_LOAN_CONDITION',
    REJECT_LOAN_CONDITION: 'REJECT_LOAN_CONDITION',
    UPDATE_LOAN_CONDITION: 'UPDATE_LOAN_CONDITION',
    ADD_BROKER_CONDITION: 'ADD_BROKER_CONDITION',
    ADD_INTERNAL_CONDITION: 'ADD_INTERNAL_CONDITION',
    ADD_CONDITION_TASKS: 'ADD_CONDITION_TASKS',
    LINK_CONDITION_TASKS: 'LINK_CONDITION_TASKS',
    UNLINK_CONDITION_TASK: 'UNLINK_CONDITION_TASK',
    REMOVE_CONDITION_TASK: 'REMOVE_CONDITION_TASK',
    DELETE_CONDITION: 'DELETE_CONDITION',
    DELETE_CONDITION_PENDING_STATUS: 'DELETE_CONDITION_PENDING_STATUS',
    DELETE_CONDITION_REJECTED_STATUS: 'DELETE_CONDITION_REJECTED_STATUS',
    ADD_CONDITIONS: 'ADD_CONDITIONS',
    ADD_CONDITIONS_PENDING_STATUS: 'ADD_CONDITIONS_PENDING_STATUS',
    ADD_CONDITIONS_REJECTED_STATUS: 'ADD_CONDITIONS_REJECTED_STATUS',
  },
);

const loanConditions = new schema.Entity('loanConditions', {}, { idAttribute: 'id' });

export const getLoanConditionByLoanIdAction = params =>
  catchAsyncActionError(params => async dispatch => {
    const { data } = await loanConditionApiV2.getCondition(params);

    return dispatch({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_CONDITIONS_BY_LOAN_ID,
      payload: normalize(data, [loanConditions]).entities.loanConditions,
    });
  })({
    ...params,
    pendingAction: () => ({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_CONDITIONS_BY_LOAN_ID_PENDING_STATUS,
    }),
    rejectedAction: () => ({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_CONDITIONS_BY_LOAN_ID_REJECTED_STATUS,
    }),
  });

export const getLoanBrokerConditionByApplicationIdAction = params =>
  catchAsyncActionError(params => async dispatch => {
    const { data } = await loanConditionApiV2.getBrokerCondition(params);

    return dispatch({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_BROKER_CONDITIONS,
      payload: normalize(data, [loanConditions]).entities.loanConditions,
    });
  })({
    ...params,
    rejectedAction: () => ({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_BROKER_CONDITIONS_REJECTED_STATUS,
    }),
  });

export const addBrokerConditionAction = catchAsyncActionError(body => async dispatch => {
  const { data } = await loanConditionApiV2.createBrokerCondition(body);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.ADD_BROKER_CONDITION,
    payload: data,
  });
});

export const addInternalConditionAction = catchAsyncActionError(body => async dispatch => {
  const { data } = await loanConditionApiV2.createBrokerCondition(body);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.ADD_INTERNAL_CONDITION,
    payload: data,
  });
});

export const addConditionTasksAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanConditionApiV2.addConditionTasks(params);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.ADD_CONDITION_TASKS,
    payload: data,
  });
});

export const linkConditionTasksAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanConditionApiV2.linkConditionTasks(params);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.LINK_CONDITION_TASKS,
    payload: data,
  });
});

export const unlinkConditionTaskAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanConditionApiV2.unlinkConditionTask(params);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.UNLINK_CONDITION_TASK,
    payload: data,
  });
});

export const removeConditionTaskAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanConditionApiV2.removeConditionTask(params);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.REMOVE_CONDITION_TASK,
    payload: data,
  });
});

export const updateBrokerConditionAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanConditionApiV2.editBrokerCondition(params);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.UPDATE_LOAN_CONDITION,
    payload: data,
  });
});

export const getLoanConditionsByIdsAction = params =>
  catchAsyncActionError(params => async dispatch => {
    const { data } = await loanConditionApiV2.getCondition(params);

    return dispatch({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_CONDITIONS_BY_IDS,
      payload: normalize(data, [loanConditions]).entities.loanConditions,
    });
  })({
    ...params,
    pendingAction: () => ({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_CONDITIONS_BY_IDS_PENDING_STATUS,
      payload: { ids: params.loanConditionId },
    }),
    rejectedAction: () => ({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_CONDITIONS_BY_IDS_REJECTED_STATUS,
      payload: { ids: params.loanConditionId },
    }),
  });

export const getLoanConditionByConditionIdAction = params =>
  catchAsyncActionError(params => async dispatch => {
    const { data } = await loanConditionApiV2.getConditionById(params);

    return dispatch({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_CONDITION_BY_CONDITION_ID,
      payload: data,
    });
  })({
    ...params,
    pendingAction: () => ({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_CONDITION_BY_CONDITION_ID_PENDING_STATUS,
      payload: { id: params.loanConditionId },
    }),
    rejectedAction: () => ({
      type: LOAN_CONDITIONS_ACTION_TYPE.GET_CONDITION_BY_CONDITION_ID_REJECTED_STATUS,
      payload: { id: params.loanConditionId },
    }),
  });

export const deleteLoanConditionAction = params =>
  catchAsyncActionError(({ loanConditionId, taskIds }) => async dispatch => {
    await loanConditionApiV2.deleteCondition({ loanConditionId });

    dispatch({
      type: TASKS_ACTION_TYPE.DELETE_TASKS,
      payload: {
        taskIds,
      },
    });

    return dispatch({
      type: LOAN_CONDITIONS_ACTION_TYPE.DELETE_CONDITION,
      payload: {
        id: loanConditionId,
        timestamp: dayjs().format(CALENDAR_LOCAL_TIME_DATE_FORMAT),
      },
    });
  })({
    ...params,
    pendingAction: () => ({
      type: LOAN_CONDITIONS_ACTION_TYPE.DELETE_CONDITION_PENDING_STATUS,
      payload: { id: params.loanConditionId },
    }),
    rejectedAction: () => ({
      type: LOAN_CONDITIONS_ACTION_TYPE.DELETE_CONDITION_REJECTED_STATUS,
      payload: { id: params.loanConditionId },
    }),
  });

export const signOffLoanConditionAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanConditionApiV2.editCondition(params);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.SIGN_OFF_LOAN_CONDITION,
    payload: data,
  });
});

export const unsignOffLoanConditionAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanConditionApiV2.unsignOffCondition(params);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.UNSIGN_OFF_LOAN_CONDITION,
    payload: data,
  });
});

export const rejectLoanConditionAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanConditionApiV2.rejectCondition(params);

  return dispatch({
    type: LOAN_CONDITIONS_ACTION_TYPE.REJECT_LOAN_CONDITION,
    payload: data,
  });
});

export const updateLoanConditionAction = data => ({
  type: LOAN_CONDITIONS_ACTION_TYPE.UPDATE_LOAN_CONDITION,
  payload: data,
});

export const addLoanConditionsAction = params =>
  catchAsyncActionError(params => async dispatch => {
    await loanConditionApiV2.createCondition(params);

    return dispatch({
      type: LOAN_CONDITIONS_ACTION_TYPE.ADD_CONDITIONS,
    });
  })({
    ...params,
    pendingAction: () => ({ type: LOAN_CONDITIONS_ACTION_TYPE.ADD_CONDITIONS_PENDING_STATUS }),
    rejectedAction: () => ({ type: LOAN_CONDITIONS_ACTION_TYPE.ADD_CONDITIONS_REJECTED_STATUS }),
  });
